import type { TFunction } from 'i18next';

import { GridRecord } from '../../../common/components/Grid/GridDataTypes';
import numberFormatter from '../../../common/numberFormatter';
import { GetEstimateByMonthWithItemsDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import mathUtils from '../../../utils/mathUtils';
import momencik from '../../../common/momencik';
import { TKeys } from '../../../translations';

interface GroupedResult {
  clientName: string;
  month: string;
  salesNet: number;
  salesCommissionAmount: number;
  salesNetTotal: number;
  salesInvoicesSum: number;
  purchaseNetTotal: number;
  purchaseInvoicesSum: number;
  purchaseAmountToAccept: number;
}

const groupByClients = (estimates: GetEstimateByMonthWithItemsDto[]) => {
  const groupedData: Record<string, GroupedResult> = {};
  for (const estimate of estimates) {
    if (!estimate.estimateItems) {
      continue;
    }
    for (const item of estimate.estimateItems) {
      const clientName = item.position.client.name;
      const month = momencik(estimate.date, 'YYYY-MM');
      const key = `${clientName}_${month}`;
      if (!groupedData[key]) {
        groupedData[key] = {
          clientName,
          month,
          salesNet: 0,
          salesCommissionAmount: 0,
          salesNetTotal: 0,
          salesInvoicesSum: 0,
          purchaseNetTotal: 0,
          purchaseInvoicesSum: 0,
          purchaseAmountToAccept: 0,
        };
      }
      groupedData[key].salesNet = mathUtils.add(
        groupedData[key].salesNet,
        mathUtils.round(item.salesNet),
      );
      groupedData[key].salesCommissionAmount = mathUtils.add(
        groupedData[key].salesCommissionAmount,
        mathUtils.round(item.salesCommissionAmount),
      );
      groupedData[key].salesNetTotal = mathUtils.add(
        groupedData[key].salesNetTotal,
        mathUtils.round(item.salesNetTotal),
      );
      groupedData[key].salesInvoicesSum = mathUtils.add(
        groupedData[key].salesInvoicesSum,
        mathUtils.round(item.salesInvoicesSum),
      );
      groupedData[key].purchaseNetTotal = mathUtils.add(
        groupedData[key].purchaseNetTotal,
        mathUtils.round(item.purchaseNetTotal),
      );
      groupedData[key].purchaseInvoicesSum = mathUtils.add(
        groupedData[key].purchaseInvoicesSum,
        mathUtils.round(item.purchaseInvoicesSum),
      );
      groupedData[key].purchaseAmountToAccept = mathUtils.add(
        groupedData[key].purchaseAmountToAccept,
        mathUtils.round(item.salesNet),
      );
    }
  }
  return groupedData;
};

export const getItemsWithClientsGridData = (
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  estimates: GetEstimateByMonthWithItemsDto[],
) => {
  const data: GridRecord[] = [];
  const groupedData: Record<string, GroupedResult> = groupByClients(estimates);

  for (const key in groupedData) {
    const item = groupedData[key];
    data.push({
      key: key,
      values: {
        publisher: item.clientName,
        month: item.month,
        salesNet: numberFormatter(item.salesNet, true),
        salesCommissionAmount: numberFormatter(
          item.salesCommissionAmount,
          true,
        ),
        salesNetTotal: numberFormatter(item.salesNetTotal, true),
        salesInvoicesSum: numberFormatter(item.salesInvoicesSum, true),
        purchaseNetTotal: numberFormatter(item.purchaseNetTotal, true),
        purchaseInvoicesSum: numberFormatter(item.purchaseInvoicesSum, true),
        purchaseAmountToAccept: numberFormatter(
          mathUtils.subtract(item.purchaseNetTotal, item.purchaseInvoicesSum),
          true,
        ),
        margin: numberFormatter(
          mathUtils.subtract(item.salesNetTotal, item.purchaseNetTotal),
          true,
        ),
      },
      orderByNumberValues: {
        salesNet: item.salesNet,
        salesCommissionAmount: item.salesCommissionAmount,
        salesNetTotal: item.salesNetTotal,
        salesInvoicesSum: item.salesInvoicesSum,
        purchaseNetTotal: item.purchaseNetTotal,
        purchaseInvoicesSum: item.purchaseInvoicesSum,
        purchaseAmountToAccept: mathUtils.subtract(
          item.purchaseNetTotal,
          item.purchaseInvoicesSum,
        ),
      },
      tdCssClass: {
        salesNet: 'amounts-align-right',
        salesCommissionAmount: 'amounts-align-right',
        salesNetTotal: 'amounts-align-right',
        salesInvoicesSum: 'amounts-align-right',
        purchaseNetTotal: 'amounts-align-right',
        purchaseInvoicesSum: 'amounts-align-right',
        purchaseAmountToAccept: 'amounts-align-right',
        margin: 'amounts-align-right',
      },
    });
  }
  return data;
};

export const getItemsWithClientsGridSummary = (
  t: TFunction<'projectLng', 'projectLng'>,
  tk: TKeys<'projectLng'>,
  data1: GetEstimateByMonthWithItemsDto[],
  keys: string[],
) => {
  const groupedData: Record<string, GroupedResult> = groupByClients(data1);
  const filteredData: Record<string, GroupedResult> = {};
  keys.forEach(key => {
    if (key in groupedData) {
      filteredData[key] = groupedData[key];
    }
  });

  let salesNet = 0;
  let salesCommissionAmount = 0;
  let salesNetTotal = 0;
  let salesInvoicesSum = 0;
  let purchaseNetTotal = 0;
  let purchaseInvoicesSum = 0;
  let purchaseAmountToAccept = 0;

  for (const key in filteredData) {
    const item = filteredData[key];
    salesNet = mathUtils.add(salesNet, mathUtils.round(item.salesNet));
    salesCommissionAmount = mathUtils.add(
      salesCommissionAmount,
      mathUtils.round(item.salesCommissionAmount),
    );
    salesNetTotal = mathUtils.add(
      salesNetTotal,
      mathUtils.round(item.salesNetTotal),
    );
    salesInvoicesSum = mathUtils.add(
      salesInvoicesSum,
      mathUtils.round(item.salesInvoicesSum),
    );
    purchaseNetTotal = mathUtils.add(
      purchaseNetTotal,
      mathUtils.round(item.purchaseNetTotal),
    );
    purchaseInvoicesSum = mathUtils.add(
      purchaseInvoicesSum,
      mathUtils.round(item.purchaseInvoicesSum),
    );
    purchaseAmountToAccept = mathUtils.add(
      purchaseAmountToAccept,
      mathUtils.round(item.salesNet),
    );
  }

  return {
    key: 'summary',
    values: {
      publisher: t(tk.common.sumUpperCase),
      month: '',
      salesNet: numberFormatter(salesNet, true),
      salesCommissionAmount: numberFormatter(salesCommissionAmount, true),
      salesNetTotal: numberFormatter(salesNetTotal, true),
      salesInvoicesSum: numberFormatter(salesInvoicesSum, true),
      purchaseNetTotal: numberFormatter(purchaseNetTotal, true),
      purchaseInvoicesSum: numberFormatter(purchaseInvoicesSum, true),
      purchaseAmountToAccept: numberFormatter(
        mathUtils.subtract(purchaseNetTotal, purchaseInvoicesSum),
        true,
      ),
      margin: numberFormatter(
        mathUtils.subtract(salesNetTotal, purchaseNetTotal),
        true,
      ),
    },
    tdCssClass: {
      salesNet: 'amounts-align-right',
      salesCommissionAmount: 'amounts-align-right',
      salesNetTotal: 'amounts-align-right',
      salesInvoicesSum: 'amounts-align-right',
      purchaseNetTotal: 'amounts-align-right',
      purchaseInvoicesSum: 'amounts-align-right',
      purchaseAmountToAccept: 'amounts-align-right',
      margin: 'amounts-align-right',
    },
  };
};
